import DataObjectIcon from '@mui/icons-material/DataObject';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import GavelIcon from '@mui/icons-material/Gavel';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { Box, ButtonBase, Typography } from '@mui/material';

export const GRADER_PATH = 'grader.json';

const iconFor = (path: string) => {
  if (path === GRADER_PATH) {
    return GavelIcon;
  }
  if (path.endsWith('.md')) {
    return MenuBookIcon;
  }
  if (path.endsWith('.json')) {
    return DataObjectIcon;
  }
  return DescriptionOutlinedIcon;
};

const ExplorerRow = ({
  depth,
  label,
  path,
  Icon,
  selected,
  onClick,
}: {
  depth: number;
  label: string;
  path?: string;
  Icon: typeof FolderOpenIcon;
  selected?: boolean;
  onClick?: () => void;
}) => (
  <ButtonBase
    disabled={!onClick}
    onClick={onClick}
    data-testid={path ? `explorer-${path}` : undefined}
    sx={{
      width: '100%',
      justifyContent: 'flex-start',
      gap: 0.75,
      px: 1,
      py: 0.5,
      pl: 1 + depth * 1.5,
      borderLeft: '2px solid',
      borderColor: selected ? 'primary.main' : 'transparent',
      backgroundColor: selected ? 'action.selected' : 'transparent',
      color: selected ? 'text.primary' : 'text.secondary',
      '&:hover': { backgroundColor: 'action.hover' },
      '&.Mui-disabled': { color: 'text.secondary' },
    }}
  >
    <Icon sx={{ fontSize: 16, flexShrink: 0 }} />
    <Typography
      variant='body2'
      noWrap
      sx={{ fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace' }}
    >
      {label}
    </Typography>
  </ButtonBase>
);

// ponytail: folders render permanently expanded. Tasks are a handful of files
// one level deep; add collapse state only if authors nest deeper.
export const FileExplorer = ({
  rootLabel,
  paths,
  activePath,
  onSelect,
}: {
  rootLabel: string;
  paths: string[];
  activePath: string;
  onSelect: (path: string) => void;
}) => {
  const rootFiles = paths.filter((path) => !path.includes('/'));
  const folders = paths.reduce((acc, path) => {
    const slash = path.lastIndexOf('/');
    if (slash === -1) {
      return acc;
    }
    const dir = path.slice(0, slash);
    acc.set(dir, [...(acc.get(dir) ?? []), path]);
    return acc;
  }, new Map<string, string[]>());

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'auto',
        backgroundColor: 'background.paper',
        borderRight: '1px solid',
        borderColor: 'divider',
        py: 1,
      }}
    >
      <Typography
        variant='overline'
        sx={{ px: 1.5, color: 'text.secondary', letterSpacing: 1 }}
      >
        Explorer
      </Typography>
      <ExplorerRow depth={0} label={rootLabel} Icon={FolderOpenIcon} />
      {rootFiles.map((path) => (
        <ExplorerRow
          key={path}
          depth={1}
          label={path}
          path={path}
          Icon={iconFor(path)}
          selected={path === activePath}
          onClick={() => onSelect(path)}
        />
      ))}
      {[...folders.entries()].map(([dir, dirPaths]) => (
        <Box key={dir}>
          <ExplorerRow depth={1} label={dir} Icon={FolderOpenIcon} />
          {dirPaths.map((path) => (
            <ExplorerRow
              key={path}
              depth={2}
              label={path.slice(dir.length + 1)}
              path={path}
              Icon={iconFor(path)}
              selected={path === activePath}
              onClick={() => onSelect(path)}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
};
